import React from 'react';
import classnames from 'classnames';
import { range } from 'lodash';
import Tooltip from 'components/common/Tooltip';
import { SNAPSHOT_TEST } from 'utils/constants';
import { WeekBelow, WeekAbove } from './Week';
import { IProps } from './types';
import styles from './styles.module.scss';

/**
 * Displays a chart that illustrates lesson completion progress.
 * @param props The props
 * @return A JSX Element
 */
function LessonSummaryChart(props: IProps): JSX.Element {
  const {
    lessonSummary: { lessons, totalWeeks },
    showHeader,
    shouldHighLightWeekNumber,
    revisitedModuleAmount,
    completedModuleAmount,
  } = props;
  let shouldShowTickIcon = true;
  let shouldShowWeekNumber = true;
  let chartType: 'rectangle' | 'square' = 'rectangle';

  if (totalWeeks >= 14) {
    shouldShowTickIcon = false;
    shouldShowWeekNumber = false;
    chartType = 'square';
  }

  return (
    <div
      className={styles.summary}
      data-testid="lesson-completed-chart"
      aria-label="Lesson Completion Chart"
    >
      <div className={classnames(styles.container, styles['grid-container'])}>
        <div className={styles.section}>
          <div className={classnames(styles['hidden-legend'])} />
          <div className={styles.topChart}>
            {showHeader && (
              <div className={styles.title}>
                <span
                  className={styles.bold}
                  data-testid="number-of-lesson-completed"
                >
                  {`${completedModuleAmount} Lessons Completed`}
                </span>
                {revisitedModuleAmount > 0 && (
                  <>
                    <span
                      className={styles['revisited-info']}
                      data-testid="number-of-lesson-revisited"
                    >
                      {`(${revisitedModuleAmount} lessons revisited)`}
                    </span>
                    {SNAPSHOT_TEST ? (
                      <span />
                    ) : (
                      <Tooltip
                        title="Lessons chart includes new and revisited lessons"
                        placement="top"
                      >
                        <div className={styles['info-icon']} />
                      </Tooltip>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className={styles['bottom-section']}>
          <div className={classnames(styles['chart-legend'])}>
            <div className={classnames(styles['goal-above-text'])}>GOAL</div>
            <div className={styles['goal-below-text']}>4 lessons per week</div>
          </div>
          <div className={styles['bottom-chart']}>
            <div className={styles.name}>Treatment Week</div>
            <div className={styles.chart}>
              <div className={styles.content}>
                {lessons.length
                  ? lessons.map((lesson) => (
                      <WeekBelow
                        key={`column-${lesson.weekNumber}`}
                        status={lesson.status}
                        lessonCompleted={lesson.lessonCompleted}
                        weekNumber={lesson.weekNumber}
                        shouldHighLightWeekNumber={shouldHighLightWeekNumber}
                        shouldShowTickIcon={shouldShowTickIcon}
                        shouldShowWeekNumber={
                          shouldShowWeekNumber || lesson.weekNumber % 2 === 0
                        }
                        chartType={chartType}
                      />
                    ))
                  : // Empty weeks below
                    range(totalWeeks).map((weekIndex) => (
                      <WeekBelow
                        key={`column-${weekIndex}`}
                        status="future"
                        lessonCompleted={0}
                        weekNumber={weekIndex + 1}
                        shouldHighLightWeekNumber={shouldHighLightWeekNumber}
                        chartType={chartType}
                      />
                    ))}
              </div>

              <div className={styles['chart-top']}>
                <div className={styles.content}>
                  {lessons.length
                    ? lessons.map((lesson) => (
                        <WeekAbove
                          key={`column-${lesson.weekNumber}`}
                          lessonCompleted={lesson.lessonCompleted}
                          chartType={chartType}
                        />
                      ))
                    : // Empty weeks above
                      range(totalWeeks).map((weekIndex) => (
                        <WeekAbove
                          key={`column-${weekIndex}`}
                          lessonCompleted={0}
                          chartType={chartType}
                        />
                      ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div />
      </div>
    </div>
  );
}

export default LessonSummaryChart;
